import _ from 'lodash';
import { hasTemplateBeat } from '@/widgets/beats/ui/reviewBeatsHelper';
import { findInBulkChanges } from '@/entities/beat';

/**
 * Adapts Holter RR heat map rows to the shape the chart expects. Copies each row and mirrors its total onto a count field.
 *
 * @param data - Heat map rows carrying a total.
 * @returns The rows with a count field added.
 */
export const formatHolterRrHeatMapCount = (data: any[]) =>
  _.map(data, (item) => ({
    ...item,
    count: item.total,
  }));

/**
 * Snaps a clicked RR position onto the fixed-width window that contains it. Floors the offset from the current start into whole intervals and returns that window start together with the start plus one interval.
 *
 * @param currentStart - Start of the current RR interval range.
 * @param positionValue - Position the new window must contain.
 * @param fixedInterval - Width of the window, defaulting to ten thousand.
 * @returns A two element array holding the new window start and end.
 */
export const calculateNewRange = (
  currentStart: number,
  positionValue: number,
  fixedInterval: number = 10000,
) => {
  const newStart =
    Math.floor((positionValue - currentStart) / fixedInterval) * fixedInterval + currentStart;
  const newEnd = newStart + fixedInterval;
  return [newStart, newEnd];
};

/**
 * Collects the ids of heat map cells that actually contain beats. Walks the rows and pushes the id of each one whose value is truthy.
 *
 * @param data - Heat map cell rows.
 * @returns The ids of the non-empty cells.
 */
export const mapFilterHeatmapIds = (data: any[]) => {
  const result: any[] = [];
  _.forEach(data, (item) => {
    if (item.value) result.push(item.id);
  });
  return result;
};

export const handleSwapsMultipleBeats = ({
  selectedBeats,
  bulkBeatsChanges,
  beatChanges,
  resetBulksBeatChanges,
  bulkSwapBeats,
  swapBeatChanges,
  resetBulkSwapBeats,
  targetTemplateId,
  type,
}: {
  selectedBeats: any[];
  bulkBeatsChanges: any;
  beatChanges: any;
  resetBulksBeatChanges: any;
  bulkSwapBeats: any;
  swapBeatChanges: any;
  resetBulkSwapBeats: any;
  targetTemplateId: any;
  type: any;
}) => {
  const cloneSwapBeatChanges: any[] = _.cloneDeep(swapBeatChanges);
  const cloneBeatChanges: any[] = _.cloneDeep(beatChanges);
  const cloneResetBulksBeatChanges: any[] = _.cloneDeep(resetBulksBeatChanges);
  const cloneResetBulkSwapBeats: any[] = _.cloneDeep(resetBulkSwapBeats);
  _.forEach(selectedBeats, (strip) => {
    _.remove(cloneResetBulkSwapBeats, (x) => x.id === strip.id);
    _.remove(cloneResetBulksBeatChanges, (x) => x.id === strip.id);
    _.remove(cloneBeatChanges, (x) => x.id === strip.id);

    const findChange = _.find(cloneSwapBeatChanges, (x) => x.id === strip.id);

    if (!findChange) {
      if (targetTemplateId !== strip.originalTemplateId) {
        cloneSwapBeatChanges.push({
          ...strip,
          templateId: targetTemplateId,
        });
      }
    } else if (findChange.originalTemplateId === targetTemplateId) {
      _.remove(cloneSwapBeatChanges, (x) => x.id === strip.id);
    } else {
      _.assign(findChange, { templateId: targetTemplateId });
    }

    if (strip.originalType === type) {
      const isInBulkBeatChange = findInBulkChanges(bulkBeatsChanges, strip);
      if (isInBulkBeatChange) {
        cloneResetBulksBeatChanges.push(strip);
      }
    }
    const inBulkSwapBeatChange = findInBulkChanges(bulkSwapBeats, strip);
    if (inBulkSwapBeatChange) {
      if (strip.templateId === targetTemplateId) {
        cloneResetBulkSwapBeats.push(strip);
      }
      if (inBulkSwapBeatChange.templateId === targetTemplateId) {
        _.remove(cloneSwapBeatChanges, (x) => x.id === strip.id);
      }
    }
  });
  return {
    newSwapBeatChanges: cloneSwapBeatChanges,
    newBeatChanges: cloneBeatChanges,
    newResetBulksBeatChanges: cloneResetBulksBeatChanges,
    newResetBulkSwapBeats: cloneResetBulkSwapBeats,
  };
};

export const handleChangeTypeMultipleBeats = ({
  selectedBeats,
  bulkBeatsChanges,
  beatChanges,
  resetBulksBeatChanges,
  bulkSwapBeats,
  swapBeatChanges,
  resetBulkSwapBeats,
  type,
  activeButton,
  isReset,
}: {
  selectedBeats: any[];
  bulkBeatsChanges: any;
  beatChanges: any;
  resetBulksBeatChanges: any;
  bulkSwapBeats: any;
  swapBeatChanges: any;
  resetBulkSwapBeats: any;
  type: any;
  activeButton: any;
  isReset?: boolean;
}) => {
  const hasTemplate = hasTemplateBeat(activeButton);
  const cloneBeatChanges: any[] = _.cloneDeep(beatChanges);

  const cloneSwapBeatChanges: any[] = _.cloneDeep(swapBeatChanges);
  const cloneResetBulksBeatChanges: any[] = _.cloneDeep(resetBulksBeatChanges);
  const cloneResetBulkSwapBeats: any[] = _.cloneDeep(resetBulkSwapBeats);
  _.forEach(selectedBeats, (strip) => {
    _.remove(cloneResetBulksBeatChanges, (x) => x.id === strip.id);

    if (hasTemplate) {
      _.remove(cloneResetBulkSwapBeats, (x) => x.id === strip.id);
      _.remove(cloneSwapBeatChanges, (x) => x.id === strip.id);
    }

    const findChange = _.find(cloneBeatChanges, (x) => x.id === strip.id);
    if (!findChange) {
      if (type !== strip.originalType && !isReset) {
        cloneBeatChanges.push({
          ...strip,
          type,
          oldType: strip.originalType,
        });
      }
    } else if (findChange.originalType === type || isReset) {
      _.remove(cloneBeatChanges, (x) => x.id === strip.id);
    } else {
      _.assign(findChange, { type });
    }

    const inBulkBeatChange = findInBulkChanges(bulkBeatsChanges, strip);
    if (inBulkBeatChange) {
      if (strip.originalType === type || isReset) {
        if (inBulkBeatChange) {
          cloneResetBulksBeatChanges.push(strip);
        }
      }
      if (inBulkBeatChange.type === type) {
        _.remove(cloneBeatChanges, (x) => x.id === strip.id);
      }
    }
    if (hasTemplate) {
      const isInBulkSwapBeatChange = findInBulkChanges(bulkSwapBeats, strip);
      if (isInBulkSwapBeatChange) {
        cloneResetBulkSwapBeats.push(strip);
      }
    }
  });
  return {
    newSwapBeatChanges: cloneSwapBeatChanges,
    newBeatChanges: cloneBeatChanges,
    newResetBulksBeatChanges: cloneResetBulksBeatChanges,
    newResetBulkSwapBeats: cloneResetBulkSwapBeats,
  };
};
